'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import { screenGenerationPrompt } from '@/shared/infrastructure/content-safety/contentSafety';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import { walletAccountId } from '@/contexts/subscriptions/services/walletAccountId';
import type { GeneratePortraitInput, GeneratePortraitPayload, Character } from '../types';
/**
 * Build the library avatar prompt: a NEUTRAL master-reference "болванка".
 * Library avatars carry only the character's identity — no art style. Book
 * generation later redraws this avatar in the book's style with the story's
 * outfit (identity from image, style/clothing from text).
 */
function buildPortraitPrompt(
  character: Character,
  customDescription?: string,
): string {
  const parts: string[] = [];

  // Use custom description or character descriptions
  if (customDescription) {
    parts.push(customDescription);
  } else if (character.visualDescription) {
    parts.push(character.visualDescription);
  } else if (character.physicalDescription) {
    parts.push(character.physicalDescription);
  }

  if (character.name) parts.push(`character named ${character.name}`);
  // Skip 'neutral' (legacy default) — it adds no information to the prompt.
  if (character.gender && character.gender.toLowerCase() !== 'neutral') {
    parts.push(`${character.gender.toLowerCase()} character`);
  }

  const desc = parts.join('. ') || 'a friendly character';

  // An INANIMATE library character (a turnip, a house — living=false) is a
  // realistic object: no face, no limbs. The book pipeline already did this
  // (CharacterReferenceService); the hub portrait gave every object a face.
  if (character.living === false) {
    return `Reference sheet of an INANIMATE OBJECT: ${desc}. ` +
      'Render as a REALISTIC object — NO face, NO eyes, NO mouth, NO arms or legs, no expression. ' +
      'Neutral clean children\'s illustration rendering with NO strong art-style signature ' +
      '(no watercolor texture, no comic ink, no anime look) — a plain, softly shaded reference. ' +
      'Whole object visible, even lighting, isolated on pure white background, ' +
      'no background elements, no scenery, no scene props, no motion, no dramatic effects, ' +
      'no text, no watermarks, G-rated, appropriate for children ages 3-10.';
  }

  return `Master character reference sheet of ${desc}. ` +
    'Neutral clean children\'s illustration rendering with NO strong art-style signature ' +
    '(no watercolor texture, no comic ink, no anime look) — a plain, softly shaded reference. ' +
    'Simple neutral everyday clothing unless the description says otherwise. ' +
    'Full-body composition showing the entire character from head to feet, neutral standing pose, ' +
    'neutral friendly expression, even lighting, isolated on pure white background, ' +
    'no background elements, no scenery, no scene props, no motion, no dramatic effects, ' +
    'no text, no watermarks, G-rated, appropriate for children ages 3-10.';
}

export async function generatePortrait(
  input: GeneratePortraitInput,
): Promise<ActionResult<GeneratePortraitPayload>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // Pre-generation IP/trademark content-safety screen of the user's free-text
    // portrait description (good-faith guardrail — reduces, not eliminates, risk).
    if (input.customDescription) {
      const screen = await screenGenerationPrompt({
        text: input.customDescription,
        context: 'portrait',
        identityId: userId,
      });
      if (!screen.allowed) return { success: false, error: screen.userMessage };
    }

    // Ownership check
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const character = await getCharacterRepository().getById(input.characterId);
    if (!character) return { success: false, error: 'Character not found' };
    if (character.familyId !== familyId) return { success: false, error: 'Forbidden' };

    // Art. 9 gate: a source photo may depict a child — require + record the affirmed
    // guardian consent before processing it. Fail closed.
    if (input.referenceImageBase64) {
      if (!input.photoConsentVersion) return { success: false, error: 'PHOTO_CONSENT_REQUIRED' };
      const { recordPhotoConsent } = await import('@/contexts/characters/services/recordPhotoConsent');
      await recordPhotoConsent(familyId, input.characterId, input.photoConsentVersion);
    }

    // Reserve tokens
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');
    const { UnifiedAIConfigService } = await import('@/contexts/ai/services/config/UnifiedAIConfigService');
    const { getImageGenerationQueue } = await import('@/contexts/ai/infrastructure/imageGenerationQueue');

    const tokenManager = getTokenManager();
    const portraitCost = await UnifiedAIConfigService.getOperationReservation('character_portrait');

    const reservation = await tokenManager.reserve({
      familyId,
      accountId: await walletAccountId(familyId),
      amount: portraitCost,
      operationType: 'character_portrait',
      metadata: {
        characterId: input.characterId,
        style: 'neutral',
        fromPhoto: !!input.referenceImageBase64,
        createdBy: userId,
      },
    });

    try {
      // GDPR / data-minimization: the real photo is TRANSIENT. Upload it to a TEMP
      // key (not a permanent per-character path) so the worker deletes it right
      // after generating the portrait (_process_portrait cleans temp refs). We keep
      // only the derived stylized portrait, never the child's photo.
      let referenceImageUrl: string | undefined;
      if (input.referenceImageBase64) {
        const { getImageStorage } = await import('@/contexts/media/storage/CharacterImageStorage');
        const buf = Buffer.from(input.referenceImageBase64, 'base64');
        referenceImageUrl = await getImageStorage().uploadTempBuffer(buf, 'image/jpeg');
      }

      // Set portraitStatus to 'generating' before enqueuing
      await getCharacterRepository().update(input.characterId, { portraitStatus: 'generating' });

      // Build prompt for thin worker — library avatars are always the
      // neutral master-reference style; input.style is ignored.
      const prompt = buildPortraitPrompt(character, input.customDescription);
      const { AIProviderConfigService } = await import('@/contexts/ai/services/config/AIProviderConfigService');
      // A library avatar IS a character portrait → the portrait engine (unset =
      // the pages engine, as before).
      const { model: imagenModel, apiVersion, provider: imagenProvider } =
        await AIProviderConfigService.getPortraitSelection();
      // Unique blob path per regeneration — never overwrite existing portraits,
      // so previously-issued SAS URLs keep resolving.
      const portraitSuffix = `neutral_${Date.now()}`;
      const blobPath = `characters/${input.characterId}/portrait_${portraitSuffix}.png`;

      const jobId = await getImageGenerationQueue().enqueue({
        type: 'character-portrait',
        characterId: input.characterId,
        familyId,
        style: 'neutral',
        reservationId: reservation.id,
        referenceImageUrl,
        customDescription: input.customDescription || undefined,
        prompt,
        imagenModel,
        imagenProvider,
        apiVersion,
        blobPath,
      });

      const updatedCharacter = await getCharacterRepository().getById(input.characterId);

      return {
        success: true,
        data: {
          character: (updatedCharacter || character) as Character,
          tokensUsed: portraitCost,
          jobId,
        },
      };
    } catch (enqueueError) {
      // Refund reservation, reset status, and notify client
      const { getEventBus } = await import('@/shared/infrastructure/events/eventBus');
      const { EVENT_TYPES } = await import('@/shared/infrastructure/events/eventTypes');
      await Promise.allSettled([
        tokenManager.refund({
          reservationId: reservation.id,
          reason: `Enqueue failed: ${(enqueueError as Error).message}`,
        }),
        getCharacterRepository().update(input.characterId, { portraitStatus: 'failed' }),
        getEventBus().emit({
          type: EVENT_TYPES.CHARACTERS.PORTRAIT_FAILED,
          familyId,
          payload: { characterId: input.characterId, familyId },
        }),
      ]);
      throw enqueueError;
    }
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generatePortrait', e) };
  }
}
